import React from 'react';
import * as Yup from 'yup';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from '../../common/withRouter';
import {
  DataDefinitionList,
  Tabs,
  EditableArea,
  Notification,
  Message,
  IconWithText,
  Loader,
  FormikInput
} from '../../web.common';
import { sentenceCase } from 'change-case';
import { LayoutContext } from '../layout/siteLayout';
import PageTop from '../layout/PageTop';
import { providerTypes } from '../../enums/ProviderTypes';
import { getApiErrorMessage } from '../../common/utils';
import withApi from '../higherOrderComponents/withApi';
import { Divider, DetailsRightBar, NoLink } from './DetailsRightBar';
import { currencies } from '../../enums/Currencies';
import {
  DisplayGuid,
  DisplayDate,
  DisplayType,
  DisplayBoolean
} from '../items';

class ProviderInternal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      providerData: null
    };

    this.saveEditedProvider = this.saveEditedProvider.bind(this);
    this.isProviderPublic = this.isProviderPublic.bind(this);
  }

  async componentDidMount() {
    const { match: { params: { providerId } } } = this.props;
    const { api: { providers: { getProvider } } } = this.props;
    const providerData = await getProvider(providerId);
    this.setState({ providerData });
  }

  async onValidationError({ validateForm }) {
    const validationErrors = await validateForm();
    const fieldsWithError = Object.keys(validationErrors).map(x => sentenceCase(x)).join(', ');
    this.setState({
      apiResponse: new Message('error', `Unable to save. Check fields: ${fieldsWithError}`)
    });
  }

  saveEditedProvider(receivedProps) {
    this.setState({ isLoading: true }, async () => {
      const {
        props: {
          language,
          api: {
            providers: {
              editProviderData
            }
          }
        }
      } = this;

      let updatedProviderData = null;
      let apiResponse;

      try {
        updatedProviderData = await editProviderData(receivedProps.values);
        apiResponse = new Message('success', 'Provider updated successfully');
      }
      catch (error) {
        this.setState({
          isLoading: false,
          apiResponse: getApiErrorMessage(error, language)
        });

        return;
      }

      this.setState({
        isLoading: false,
        providerData: updatedProviderData,
        apiResponse
      });

      receivedProps.toggleAreaEditMode();
    });
  }

  isProviderPublic(isPublic) {
    return (
      <IconWithText
        iconName={isPublic ? 'check' : 'times'}
        iconClasses={[`text-color-${isPublic ? 'success' : 'danger'}`]}
        text='Provider is public'
      />
    );
  }

  render() {
    const { isLoading, providerData, apiResponse } = this.state;

    const { categories } = this.props;
    const mappedCategories = categories.map(({ Name }) => ({ value: Name, label: Name }));
    const mappedProviderTypes = Object.entries(providerTypes)
      .map(([key]) => ({ value: key, label: key }));

    if (!providerData) {
      return <Loader />;
    }

    const {
      Id = 0,
      Name = '',
      Description = '',
      Contact: {
        Name: ContactName = '',
        PhoneNumber: {
          Number: PhoneNumber = ''
        },
        EmailAddress: {
          Email = ''
        }
      },
      Type = 'Undefined',
      Category = [],
      CreatedAtCet = '',
      CurrencyIsoCode = 'DKK',
      IsPublic = true
    } = providerData || {};

    return (
      <LayoutContext.Consumer>
        {() => (
          <React.Fragment>
            <Notification newMessage={apiResponse} />
            <div className='content-with-right-pane-delimiter'>
              <PageTop
                documentTitle='Provider page'
                documentDescription='Provider page'
              />

              <h2 className='main-page-header main-page-header-with-subheader'>
                Provider
              </h2>
              <h5 className='main-page-subheader'>{Name}</h5>
              <br />

              {
                providerData ? (
                  <EditableArea
                    ariaEditorName='editor-name'
                    onSubmit={receivedProps => this.saveEditedProvider(receivedProps)}
                    isLoading={isLoading}
                    savingText='Updating...'
                    displayButtonLabels
                    onValidationError={formikProps => this.onValidationError(formikProps)}
                    validationSchema={Yup.object().shape({
                      Name: Yup.string()
                        .trim()
                        .min(2, 'Should be at least 2 characters long')
                        .max(256, 'Should be less than 256 characters long')
                        .required('Field is Required'),
                      Description: Yup.string()
                        .trim()
                        .min(2, 'Should be at least 2 characters long')
                        .max(256, 'Should be less than 256 characters long')
                        .required('Field is Required'),
                      Email: Yup.string()
                        .trim()
                        .min(2, 'Should be at least 2 characters long')
                        .max(256, 'Should be less than 256 characters long')
                        .required('Field is Required'),
                      ContactName: Yup.string()
                        .trim()
                        .min(2, 'Should be at least 2 characters long')
                        .max(256, 'Should be less than 256 characters long')
                        .required('Field is Required'),
                      PhoneNumber: Yup.string()
                        .trim()
                        .min(2, 'Should be at least 2 characters long')
                        .max(256, 'Should be less than 256 characters long')
                        .required('Field is Required'),
                      Category: Yup.string().required('Field is Required')
                    })}
                    initialValues={{
                      Id,
                      Name,
                      ContactName,
                      PhoneNumber,
                      Email,
                      Description,
                      Type,
                      Category: Category.split(', '),
                      CreatedAtCet,
                      CurrencyIsoCode,
                      IsPublic
                    }}
                  >
                    {({ isEditMode }) => (
                      <React.Fragment>
                        <Tabs tablistAriaTitle='booking'>
                          {
                            [
                              {
                                tab: {
                                  content: (<IconWithText iconName='info' text='Main Info' />)
                                },
                                panel: {
                                  content: <DataDefinitionList
                                    classNames={['border-0', 'detail-page-list']}
                                    listData={[{
                                      Id: <DisplayGuid>{Id}</DisplayGuid>,
                                      'Name*': isEditMode
                                        ? <FormikInput name='Name' description='Name' placeholder required />
                                        : Name,
                                      'Description*': isEditMode
                                        ? (
                                          <FormikInput
                                            type='textarea'
                                            name='Description'
                                            description='Description'
                                            placeholder
                                          />
                                        )
                                        : Description,
                                      Type: isEditMode
                                        ? (
                                          <FormikInput
                                            type='select'
                                            options={mappedProviderTypes}
                                            name='Type'
                                            description='Type'
                                            placeholder
                                          />
                                        )
                                        : <DisplayType>{Type}</DisplayType>,
                                      'Category*': isEditMode
                                        ? (
                                          <FormikInput
                                            type='select'
                                            options={mappedCategories}
                                            isMulti
                                            name='Category'
                                            description='Category'
                                            placeholder
                                          />
                                        )
                                        : Category,
                                      'Currency Iso Code': isEditMode
                                        ? (
                                          <FormikInput
                                            type='select'
                                            options={currencies}
                                            name='CurrencyIsoCode'
                                            description='Currency Iso Code'
                                            placeholder
                                          />
                                        )
                                        : CurrencyIsoCode,
                                      'Is Public': isEditMode
                                        ? (
                                          <FormikInput
                                            type='checkbox'
                                            name='IsPublic'
                                            label='IsPublic'
                                            description='.'
                                            placeholder
                                          />
                                        )
                                        : <DisplayBoolean>{IsPublic}</DisplayBoolean>,
                                      'Created At (CET)': <DisplayDate>{CreatedAtCet}</DisplayDate>
                                    }]}
                                    hasWideDescriptions
                                  />
                                }
                              },
                              {
                                tab: {
                                  content: (<IconWithText iconName='address-book' text='Contact' />)
                                },
                                panel: {
                                  content: <DataDefinitionList
                                    classNames={['border-0', 'detail-page-list']}
                                    listData={[{
                                      'Email*': isEditMode
                                        ? <FormikInput name='Email' description='Email' placeholder required />
                                        : Email,
                                      'Contact Name*': isEditMode
                                        ? (
                                          <FormikInput
                                            name='ContactName'
                                            description='Contact Name'
                                            placeholder
                                            required
                                          />
                                        )
                                        : ContactName,
                                      'Phone Number*': isEditMode
                                        ? (
                                          <FormikInput
                                            name='PhoneNumber'
                                            description='Phone Number'
                                            placeholder
                                            required
                                          />
                                        )
                                        : PhoneNumber
                                    }]}
                                    hasWideDescriptions
                                  />
                                }
                              }
                            ]
                          }
                        </Tabs>
                      </React.Fragment>
                    )}
                  </EditableArea>
                ) : ''
              }
              <DetailsRightBar>
                <Divider>Links</Divider>
                <NoLink>None</NoLink>
                <Divider>Actions</Divider>
                <NoLink>None</NoLink>
              </DetailsRightBar>
            </div>
          </React.Fragment>
        )}
      </LayoutContext.Consumer>
    );
  }
}
const Provider = withRouter(withApi(
  connect(({ user, categories }) => ({ isLoggedIn: !!user, categories }), {})(ProviderInternal)
));

Provider.requiresLogin = true;
Provider.propTypes = { isLoggedIn: PropTypes.bool };

export default Provider;
